import ExperienceEntity, { ExperienceJSON, ExperienceRelationshipsJSON, ExperiencesSearchParams, ExperiencesSearchQuery, ExperienceUpdate } from '../entities/Experience';
import { getMaxThreshold, isCustomRequestProduct, isParticipantsRangeValid, sortByPackagePrice } from './products';
import { sanitize } from './strings';

export const EXPERIENCE_MIN_IMAGES = 4;
export const EXPERIENCE_MIN_DESCRIPTION_LENGTH = 500;
export const EXPERIENCE_MIN_PRODUCTS = 1;

export const experienceTypes: ExperienceJSON['type'][] = [
  'AUDIO_VISUAL',
  'BAND_DJ',
  'BAR',
  'BOX_SHIPPING',
  'BRANDED_MERCHANDISE',
  'CATERING',
  'DECORATOR',
  'DMC',
  'EVENT_AGENCY',
  'FLORIST',
  'FOOD_DELIVERY',
  'GRAPHIC_DESIGN',
  'HOST_STAFF',
  'HOTEL',
  'IN_OFFICE',
  'IN_PERSON',
  'LIGHTING',
  'MEETING_ROOM',
  'PHOTO_BOOTH',
  'PHOTOGRAPHER',
  'PRINTING',
  'RESTAURANT',
  'SECURITY',
  'SPEAKER',
  'TRANSPORT',
  'VENUE',
  'VIDEOGRAPHER',
  'VIRTUAL'
];

const LOCATED_AT_PARTNER_PLACE_TYPES = [
  'BAR',
  'HOTEL',
  'IN_PERSON',
  'MEETING_ROOM',
  'RESTAURANT',
  'VENUE'
];

export function getExperiencePath({ id, type }: Pick<ExperienceEntity, 'id' | 'type'>): string {
  return `/experiences/${type.toLowerCase().replace('_', '-')}/${id}`;
}

export function isVirtual({ type }: Pick<ExperienceJSON, 'type'>) {
  return type === 'VIRTUAL';
}

export function isBoxShipping({ type }: Pick<ExperienceJSON, 'type'>) {
  return type === 'BOX_SHIPPING';
}

export function isGeneric({ partner }: Pick<ExperienceJSON, 'partner'>) {
  return partner === null;
}

export function isLocatedAtPartnerPlace(experience: Pick<ExperienceEntity, 'type' | 'partner'>) {
  if (!LOCATED_AT_PARTNER_PLACE_TYPES.includes(experience.type)) {
    return false;
  }

  return !isGeneric(experience);
}

export function buildExperiencesSearchParams(query: ExperiencesSearchQuery): ExperiencesSearchParams {
  const params: ExperiencesSearchParams = {};

  if (query.participants) {
    params.participants = Number(query.participants);
  }

  if (query.languageId) {
    params.languageId = Number(query.languageId);
  }

  if (query.price && query.currency) {
    params.price = Number(query.price);
    params.currency = query.currency;
  }

  if ([...experienceTypes, 'PACKAGE'].includes(query.type)) {
    params.type = query.type;
  }

  if (Array.isArray(query.tagIds) && query.tagIds.length > 0) {
    params.tagIds = query.tagIds.filter(tagId => !isNaN(Number(tagId))).map(tagId => Number(tagId));
  }

  if (query.query) {
    params.query = query.query;
  }

  if (query.lat && query.long && query.radius) {
    params.location = {
      circle: {
        lat: Number(query.lat),
        long: Number(query.long),
        radius: Number(query.radius)
      }
    };
  }

  if (query.isCompanyRecommended === 'true') {
    params.isCompanyRecommended = true;
  }

  return params;
}

export function hasParticipantsOrPriceFilters({ price, currency, participants }: ExperiencesSearchParams) {
  return (currency && typeof price === 'number') || typeof participants === 'number';
}

export function isCustomRequestEnabled(experience: ExperienceJSON) {
  return experience.products.some(isCustomRequestProduct);
}

export function getExperienceMaxParticipants({ products }: ExperienceJSON) {
  return Math.max(...products.map(getMaxThreshold));
}

export function isCompanyRecommended({ partner }: ExperienceJSON, companyRecommendedPartners: ExperienceRelationshipsJSON['companyRecommendedPartners']) {
  if (!partner) {
    return false;
  }

  return companyRecommendedPartners.some(({ partnerId }) => partnerId === partner.id);
}

export function hasMap(experience: ExperienceJSON) {
  if (!LOCATED_AT_PARTNER_PLACE_TYPES.includes(experience.type)) {
    return false;
  }

  if (!experience.partner?.address) {
    return false;
  }

  if (!experience.partner?.address.lat || !experience.partner?.address.long) {
    return false;
  }

  return true;
}

export function getDefaultProductId(experience: ExperienceJSON, participants: number) {
  if (experience.products.length === 0) {
    return null;
  }

  const products = experience.products.filter(product =>
    !isCustomRequestProduct(product) &&
      isParticipantsRangeValid(product, participants)
  );

  if (products.length > 0) {
    const sortedProducts = sortByPackagePrice(products, participants);

    return sortedProducts[0].id;
  }

  return experience.products[0].id;
}

export function isDescriptionValid(experience: Pick<ExperienceJSON, 'description'>) {
  return experience.description.length >= EXPERIENCE_MIN_DESCRIPTION_LENGTH;
}

export function isImagesCountValid(experience: Pick<ExperienceJSON, 'experiencesImages'>) {
  return experience.experiencesImages.length >= EXPERIENCE_MIN_IMAGES;
}

export function isProductCountValid(experience: Pick<ExperienceJSON, 'products'>) {
  return experience.products.length >= EXPERIENCE_MIN_PRODUCTS;
}

export function cleanData<T extends ExperienceUpdate>(data: T): T {
  if (typeof data.description === 'string') {
    data.description = sanitize(data.description);
  }

  return data;
}

export function getExperienceTypeFromString(type: string | null): ExperienceJSON['type'] {
  if (!type) {
    return 'IN_PERSON';
  }

  const typeStr = type.toLowerCase();

  const hotelTypes = [
    'bed_and_breakfast',
    'campground',
    'cottage',
    'farmstay',
    'guest_house',
    'hostel',
    'hotel',
    'inn',
    'lodging',
    'motel',
    'resort'
  ];
  if (hotelTypes.some(term => typeStr.includes(term))) {
    return 'HOTEL';
  }

  const restaurantTypes = [
    'cafeteria',
    'diner',
    'food_court',
    'restaurant',
    'steak_house'
  ];
  if (restaurantTypes.some(term => typeStr.includes(term))) {
    return 'RESTAURANT';
  }

  const barTypes = [
    'bar',
    'cafe',
    'night_club',
    'pub',
    'wine_bar'
  ];
  if (barTypes.some(term => typeStr.includes(term))) {
    return 'BAR';
  }

  const cateringTypes = [
    'catering_service',
    'meal_takeaway'
  ];
  if (cateringTypes.some(term => typeStr.includes(term))) {
    return 'CATERING';
  }

  const venueTypes = [
    'amphitheatre',
    'auditorium',
    'banquet_hall',
    'community_center',
    'concert_hall',
    'convention_center',
    'cultural_center',
    'opera_house',
    'philharmonic_hall',
    'venue'
  ];
  if (venueTypes.some(term => typeStr.includes(term))) {
    return 'VENUE';
  }

  const foodDeliveryTypes = [
    'food_delivery',
    'meal_delivery'
  ];
  if (foodDeliveryTypes.some(term => typeStr.includes(term))) {
    return 'FOOD_DELIVERY';
  }

  const floristTypes = [
    'florist',
  ];
  if (floristTypes.some(term => typeStr.includes(term))) {
    return 'FLORIST';
  }

  const meetingRoomTypes = [
    'coworking_space',
    'meeting_room',
  ];
  if (meetingRoomTypes.some(term => typeStr.includes(term))) {
    return 'MEETING_ROOM';
  }

  const transportTypes = [
    'chauffeur_service',
    'ferry_service',
    'taxi_service',
    'transportation_service',
    'travel_agency',
  ];
  if (transportTypes.some(term => typeStr.includes(term))) {
    return 'TRANSPORT';
  }

  return 'IN_PERSON';
}